import type { Locale } from './locale';

/**
 * Locale-aware presentation of dates, numbers, relative time, byte sizes and durations through `Intl`.
 */
export class Formats {
	#locale: Locale;
	#clock: () => number;

	/**
	 * @param clock Supplies the present for relative times; tests fix it
	 */
	constructor(locale: Locale, clock: () => number = Date.now) {
		this.#locale = locale;
		this.#clock = clock;
	}

	#time(value: string | number | Date) {
		return value instanceof Date ? value : new Date(value);
	}

	date = (value: string | number | Date) => new Intl.DateTimeFormat(this.#locale.code, { dateStyle: 'medium' }).format(this.#time(value));

	/** A calendar day in UTC, as the periods of usage are counted: no hour that only a time zone adds */
	day = (value: string | number | Date) => new Intl.DateTimeFormat(this.#locale.code, { dateStyle: 'medium', timeZone: 'UTC' }).format(this.#time(value));

	moment = (value: string | number | Date) => new Intl.DateTimeFormat(this.#locale.code, { dateStyle: 'medium', timeStyle: 'short' }).format(this.#time(value));

	clock = (value: string | number | Date) => new Intl.DateTimeFormat(this.#locale.code, { timeStyle: 'medium' }).format(this.#time(value));

	number = (value: number, options?: Intl.NumberFormatOptions) => new Intl.NumberFormat(this.#locale.code, options).format(value);

	percent = (ratio: number) => new Intl.NumberFormat(this.#locale.code, { style: 'percent', maximumFractionDigits: 0 }).format(ratio);

	/**
	 * "3 minutes ago", "in 2 days": the largest unit that gives a whole value
	 */
	relative = (value: string | number | Date) => {
		const seconds = Math.round((this.#time(value).getTime() - this.#clock()) / 1000);
		const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [['day', 86400], ['hour', 3600], ['minute', 60], ['second', 1]];
		const [unit, size] = units.find(([, span]) => Math.abs(seconds) >= span) ?? units[3];
		return new Intl.RelativeTimeFormat(this.#locale.code, { numeric: 'auto' }).format(Math.trunc(seconds / size), unit);
	};

	/**
	 * Binary multiples with the unit names of the language: `1.5 MB`, `1,5 MB`
	 */
	bytes = (value: number) => {
		const units = ['byte', 'kilobyte', 'megabyte', 'gigabyte', 'terabyte'] as const;
		const index = value > 0 ? Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1) : 0;
		// Bytes are written out ("402 bytes"): the short form has no plural in English ("402 byte", audit CDN-56)
		return new Intl.NumberFormat(this.#locale.code, { style: 'unit', unit: units[index], unitDisplay: index ? 'short' : 'long', maximumFractionDigits: index ? 1 : 0 }).format(value / 1024 ** index);
	};

	/**
	 * A measured span from milliseconds: `850 ms`, `14.1 s`, `1 min 1 s`
	 */
	duration = (milliseconds: number) => {
		const unit = (value: number, name: string, digits = 0) => new Intl.NumberFormat(this.#locale.code, { style: 'unit', unit: name, unitDisplay: 'short', maximumFractionDigits: digits }).format(value);
		if (milliseconds < 1000) return unit(milliseconds, 'millisecond');
		if (milliseconds < 60000) return unit(milliseconds / 1000, 'second', 1);
		const minutes = Math.floor(milliseconds / 60000);
		const seconds = Math.round((milliseconds % 60000) / 1000);
		if (minutes < 60) return seconds ? `${unit(minutes, 'minute')} ${unit(seconds, 'second')}` : unit(minutes, 'minute');
		return `${unit(Math.floor(minutes / 60), 'hour')} ${unit(minutes % 60, 'minute')}`;
	};

	/**
	 * Time that is passing, in whole seconds so a counter does not flicker: `8 s`, `2 min 5 s`, `14 min`
	 */
	elapsed = (milliseconds: number) => {
		const seconds = Math.max(0, Math.floor(milliseconds / 1000));
		if (seconds < 600) return this.duration(seconds < 60 ? seconds * 1000 : Math.floor(seconds / 60) * 60000 + (seconds % 60) * 1000);
		return this.duration(Math.floor(seconds / 60) * 60000);
	};

	/**
	 * An expected duration, rounded as a person says it: `5 s`, `30 s`, `2 min`, `1 h 10 min`
	 */
	estimate = (milliseconds: number) => {
		if (milliseconds < 10000) return this.duration(Math.max(1, Math.round(milliseconds / 1000)) * 1000);
		if (milliseconds < 60000) return this.duration(Math.round(milliseconds / 5000) * 5000);
		return this.duration(Math.max(1, Math.round(milliseconds / 60000)) * 60000);
	};
}
